import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import RenderInput from '../RenderInput';
import { formatMoney } from '../../utils/mathUtils';

type AmountInputProps = {
    name: string;
    value: number;
    label?: string;
    helperText?: string;
    onChange: (newValue: number) => void;
    className?: string;
    disabled?: boolean;
    variant?: string;
    fullWidth?: boolean;
    placeholder?: string;
    required?: boolean;
};

const AmountInput: FC<AmountInputProps> = ({
    name,
    value,
    label,
    helperText,
    onChange,
    className,
    disabled,
    variant = 'outlined',
    fullWidth = true,
    placeholder = '0',
    required
}) => {
    const [display, setDisplay] = useState<string>(formatMoney(value));
    const isEditingRef = useRef(false);
    const latestValueRef = useRef(value);

    useEffect(() => {
        // If value changed externally (form reset, rate change, etc.), always update
        if (value !== latestValueRef.current || !isEditingRef.current) {
            latestValueRef.current = value;
            isEditingRef.current = false;
            setDisplay(formatMoney(value));
        }
    }, [value]);

    const handleFocus = useCallback(() => {
        isEditingRef.current = true;
        setDisplay(latestValueRef.current ? String(latestValueRef.current) : '');
    }, []);

    const handleBlur = useCallback(() => {
        isEditingRef.current = false;
        setDisplay(formatMoney(latestValueRef.current));
    }, []);

    const handleChange = useCallback(
        (e: any) => {
            const inputVal = String(e.target?.value || e).replace(/,/g, '');
            // Only allow digits, decimal point, and minus sign
            const rawStr = inputVal.replace(/[^0-9.\-]/g, '');
            setDisplay(rawStr);
            const raw = parseFloat(rawStr) || 0;
            latestValueRef.current = raw;
            onChange(raw);
        },
        [onChange]
    );

    return (
        <RenderInput
            field={{
                name,
                value: display,
                onFocus: handleFocus,
                onBlur: handleBlur
            }}
            type="tel"
            label={label}
            className={className}
            placeholder={placeholder}
            handleChangeCallback={handleChange}
            variant={variant}
            fullWidth={fullWidth}
            disabled={disabled}
            required={required}
            helperText={helperText}
            InputLabelProps={{ shrink: true }}
        />
    );
};

export default AmountInput;
